export interface DesignTokens {
  mode: 'dark' | 'light';
  density: 'comfortable' | 'compact';
  colors: {
    bg: {
      app: string;
      panel: string;
      panelHeader: string;
      elevated: string;
      card: string;
      cardHover: string;
      input: string;
      backdrop: string;
    };
    text: {
      primary: string;
      secondary: string;
      muted: string;
      inverse: string;
    };
    border: {
      default: string;
      subtle: string;
      strong: string;
      focus: string;
    };
    brand: {
      primary: string;
      primaryHover: string;
      primaryGlow: string;
      primarySubtle: string;
      accent: string;
    };
    gene: {
      greenBg: string;
      greenText: string;
      greenBorder: string;
      redBg: string;
      redText: string;
      redBorder: string;
      emptyBg: string;
      emptyText: string;
      donorBorder: string;
      highlightBorder: string;
    };
    status: {
      success: string;
      successBg: string;
      warning: string;
      warningBg: string;
      error: string;
      errorBg: string;
      info: string;
      infoBg: string;
    };
  };
  spacing: {
    cardPadding: number;
    gap: number;
    borderRadius: number;
    rowHeight: number;
  };
}

export const darkTokens: DesignTokens = {
  mode: 'dark',
  density: 'comfortable',
  colors: {
    bg: {
      app: '#202020',
      panel: '#272727',
      panelHeader: '#303030',
      elevated: '#303030',
      card: '#272727',
      cardHover: '#383838',
      input: '#303030',
      backdrop: 'rgba(10, 10, 10, 0.85)'
    },
    text: {
      primary: '#F3F3F3',
      secondary: '#A6A6A6',
      muted: '#A6A6A6',
      inverse: '#202020'
    },
    border: {
      default: '#414141',
      subtle: '#383838',
      strong: '#545454',
      focus: '#60CDFF'
    },
    brand: {
      primary: '#60CDFF',
      primaryHover: '#7AD5FF',
      primaryGlow: 'rgba(96, 205, 255, 0)',
      primarySubtle: 'rgba(96, 205, 255, 0.13)',
      accent: '#60CDFF'
    },
    gene: {
      greenBg: '#4A7C17',
      greenText: '#FFFFFF',
      greenBorder: '#598518',
      redBg: '#8A2E22',
      redText: '#FFFFFF',
      redBorder: '#94382A',
      emptyBg: '#2A2A2A',
      emptyText: '#777777',
      donorBorder: '#FFD700',
      highlightBorder: '#60CDFF'
    },
    status: {
      success: '#4CAF50',
      successBg: 'rgba(76, 175, 80, 0.12)',
      warning: '#FFA726',
      warningBg: 'rgba(255, 167, 38, 0.12)',
      error: '#E53935',
      errorBg: 'rgba(229, 57, 53, 0.12)',
      info: '#60CDFF',
      infoBg: 'rgba(96, 205, 255, 0.13)'
    }
  },
  spacing: {
    cardPadding: 16,
    gap: 16,
    borderRadius: 4,
    rowHeight: 32
  }
};

export const lightTokens: DesignTokens = {
  mode: 'light',
  density: 'comfortable',
  colors: {
    bg: {
      app: '#F4F6F8',
      panel: '#FFFFFF',
      panelHeader: '#F8FAFC',
      elevated: '#FFFFFF',
      card: '#FFFFFF',
      cardHover: '#F8FAFC',
      input: '#F1F5F9',
      backdrop: 'rgba(15, 23, 42, 0.6)'
    },
    text: {
      primary: '#1E293B',
      secondary: '#475569',
      muted: '#526174',
      inverse: '#FFFFFF'
    },
    border: {
      default: '#E2E8F0',
      subtle: '#EDF2F7',
      strong: '#CBD5E1',
      focus: '#0284C7'
    },
    brand: {
      primary: '#0369A1',
      primaryHover: '#075985',
      primaryGlow: 'rgba(2, 132, 199, 0.2)',
      primarySubtle: 'rgba(2, 132, 199, 0.08)',
      accent: '#D97706'
    },
    gene: {
      greenBg: '#3F7013',
      greenText: '#FFFFFF',
      greenBorder: '#4E881A',
      redBg: '#9B2C1F',
      redText: '#FFFFFF',
      redBorder: '#B91C1C',
      emptyBg: '#E2E8F0',
      emptyText: '#64748B',
      donorBorder: '#B45309',
      highlightBorder: '#0284C7'
    },
    status: {
      success: '#166534',
      successBg: 'rgba(22, 163, 74, 0.12)',
      warning: '#B45309',
      warningBg: 'rgba(217, 119, 6, 0.12)',
      error: '#DC2626',
      errorBg: 'rgba(220, 38, 38, 0.12)',
      info: '#0284C7',
      infoBg: 'rgba(2, 132, 199, 0.12)'
    }
  },
  spacing: {
    cardPadding: 16,
    gap: 16,
    borderRadius: 4,
    rowHeight: 32
  }
};

export const getTokens = (mode: 'dark' | 'light', density: 'comfortable' | 'compact' = 'comfortable'): DesignTokens => {
  const base = mode === 'dark' ? darkTokens : lightTokens;
  if (density === 'compact') {
    return {
      ...base,
      density: 'compact',
      spacing: {
        cardPadding: 10,
        gap: 10,
        borderRadius: 4,
        rowHeight: 26
      }
    };
  }
  return base;
};
